import React, { useEffect, useState } from "react";
import ColorVariantsEditor from "./ColorVariantsEditor";
import DesignersEditor from "./DesignersEditor";
import { API_URL } from "../../config";
import { authHeader } from "../../auth";
import styles from "./ProductEditModal.module.css";

const cloudinaryCloudName = "dk1nepgzk";
const uploadPreset = "drxz9h8c";

async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`,
    { method: "POST", body: formData }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error?.message || "Image upload failed");
  }
  return data.secure_url;
}

// Edit an existing product. `categories` and `collections` come from the
// parent page. The legacy brand value is preserved untouched behind the
// scenes — classification now happens via collection + category.
const ProductEditModal = ({ product, categories, collections = [], onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [designers, setDesigners] = useState([]);
  const [colors, setColors] = useState([]);
  const [colorsUploading, setColorsUploading] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [newCoverPreview, setNewCoverPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!product) return;
    setForm({
      name: product.name || "",
      brand: product.brand || "",
      price: product.price ?? "",
      stockInCount: product.stockInCount ?? "",
      description: product.description || "",
      richDescription: product.richDescription || "",
      // populated refs come back as objects — we need their ids
      category: product.category?._id || product.category || "",
      productCollection:
        product.productCollection?._id || product.productCollection || "",
      gender: product.gender || "",
      active: product.active !== false,
      isFeatured: !!product.isFeatured,
      outOfStock: !!product.outOfStock,
    });
    setExistingImages(product.images || []);
    setDesigners(
      (product.designers || []).map((d) => ({ name: d.name || "", link: d.link || "" }))
    );
    setColors(
      (product.colors || []).map((c) => ({
        name: c.name || "",
        hex: c.hex || "#141210",
        image: c.image || "",
        images: c.images || [],
      }))
    );
    setNewFiles([]);
    setNewPreviews([]);
    setNewCoverFile(null);
    setNewCoverPreview("");
    setError("");
  }, [product]);

  if (!product) return null;

  const setField = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewCoverFile(file);
    setNewCoverPreview(URL.createObjectURL(file));
  };

  const handleGalleryAdd = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setNewFiles((prev) => [...prev, ...files]);
    setNewPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setError("");
    if (!form.category) {
      setError("Pick a category.");
      return;
    }
    setSaving(true);
    try {
      const image = newCoverFile ? await uploadToCloudinary(newCoverFile) : product.image;
      const uploaded = newFiles.length
        ? await Promise.all(newFiles.map(uploadToCloudinary))
        : [];

      const payload = {
        ...form,
        price: Number(form.price),
        stockInCount: Number(form.stockInCount),
        image,
        images: [...existingImages, ...uploaded],
        // a named colour counts even without photos (swatch-only)
        colors: colors.filter((c) => c.name.trim()),
        designers: designers.filter((d) => d.name.trim()),
      };

      const response = await fetch(`${API_URL}/products/${product._id || product.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        let message;
        try {
          message = JSON.parse(text).message;
        } catch {
          message = text;
        }
        throw new Error(message || "Could not save the product.");
      }

      onSaved(form.name);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <h2 className={styles.headTitle}>Edit Product</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.body}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.coverRow}>
            <img
              src={newCoverPreview || product.image}
              alt={form.name}
              className={styles.coverThumb}
            />
            <label className={styles.replaceBtn}>
              Replace cover image
              <input type="file" accept="image/*" onChange={handleCoverChange} />
            </label>
          </div>

          <p className={styles.sectionLabel}>Gallery</p>
          <div className={styles.galleryGrid}>
            {existingImages.map((url, index) => (
              <div key={url} className={styles.galleryItem}>
                <img src={url} alt={`Gallery ${index + 1}`} />
                <button
                  type="button"
                  className={styles.galleryRemove}
                  onClick={() => removeExistingImage(index)}
                  aria-label={`Remove image ${index + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
            {newPreviews.map((url, index) => (
              <div key={url} className={styles.galleryItem}>
                <img src={url} alt={`New ${index + 1}`} />
                <button
                  type="button"
                  className={styles.galleryRemove}
                  onClick={() => removeNewImage(index)}
                  aria-label={`Remove new image ${index + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
            <label className={styles.galleryAdd}>
              ＋
              <input type="file" accept="image/*" multiple onChange={handleGalleryAdd} />
            </label>
          </div>

          <div className={styles.grid}>
            <div className={styles.fieldWide}>
              <label className={styles.label} htmlFor="e-name">Name</label>
              <input id="e-name" className={styles.input} value={form.name} onChange={setField("name")} />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="e-category">Category</label>
              <select
                id="e-category"
                className={styles.select}
                value={form.category}
                onChange={setField("category")}
              >
                <option value="" disabled>
                  Select a category
                </option>
                {categories.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="e-collection">Collection</label>
              <select
                id="e-collection"
                className={styles.select}
                value={form.productCollection}
                onChange={setField("productCollection")}
              >
                <option value="">None</option>
                {collections.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="e-price">Price (₦)</label>
              <input
                id="e-price"
                className={styles.input}
                type="number"
                min="0"
                step="any"
                value={form.price}
                onChange={setField("price")}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="e-stock">Stock count</label>
              <input
                id="e-stock"
                className={styles.input}
                type="number"
                min="0"
                value={form.stockInCount}
                onChange={setField("stockInCount")}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="e-gender">Gender</label>
              <select
                id="e-gender"
                className={styles.select}
                value={form.gender}
                onChange={setField("gender")}
              >
                <option value="">Unisex / not specified</option>
                <option value="Men">Men</option>
                <option value="Women">Women</option>
              </select>
            </div>

            <div className={styles.fieldWide}>
              <label className={styles.label}>Designers / Collaborators</label>
              <DesignersEditor designers={designers} onChange={setDesigners} />
            </div>

            <div className={styles.fieldWide}>
              <label className={styles.label} htmlFor="e-desc">Description</label>
              <textarea
                id="e-desc"
                className={styles.textarea}
                value={form.description}
                onChange={setField("description")}
              />
            </div>

            <div className={styles.fieldWide}>
              <label className={styles.label} htmlFor="e-rich">Rich description</label>
              <textarea
                id="e-rich"
                className={styles.textarea}
                value={form.richDescription}
                onChange={setField("richDescription")}
              />
            </div>

            <div className={styles.fieldWide}>
              <p className={styles.label} style={{ margin: 0 }}>Colours</p>
              <ColorVariantsEditor
                colors={colors}
                onChange={setColors}
                onError={setError}
                onBusyChange={setColorsUploading}
              />
            </div>

            <div className={styles.toggleRow}>
              <label className={styles.toggle}>
                <input type="checkbox" checked={form.isFeatured} onChange={setField("isFeatured")} />
                Featured
              </label>
              <label className={styles.toggle}>
                <input type="checkbox" checked={form.outOfStock} onChange={setField("outOfStock")} />
                Out of stock
              </label>
              <label className={styles.toggle}>
                <input type="checkbox" checked={form.active} onChange={setField("active")} />
                Visible on the store
              </label>
            </div>
          </div>
        </div>

        <div className={styles.foot}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving || colorsUploading}
          >
            {saving
              ? "Saving…"
              : colorsUploading
              ? "Colour photos uploading…"
              : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductEditModal;
